import { gameSyncState } from './server-state';
import { isBackendEnabled } from '../net/backend';
import { emitGame, GameEvents, refreshHudCoalesced } from './events';

export type InventorySlot = { name: string; count: number; isDefault?: boolean; weaponId?: number; itemId?: number };

export const ENERGY_PER_TAP = 1;
const LOCAL_ENERGY_REGEN_SECONDS = 30;
const INVENTORY_SLOT_COUNT = 9;

export const playerState = {
  energy: 100,
  maxEnergy: 100,
  dust: 0,
  dustCollected: 0,
  nextEnergyAt: null as string | null,
  energyUpdatedAt: null as string | null,
  equipped: {
    head: null as string | null,
    body: null as string | null,
    weapon: 'WoodClub' as string | null,
  },
  inventory: {} as Record<string, number>,
  inventorySlots: Array.from({ length: INVENTORY_SLOT_COUNT }, () => null) as Array<InventorySlot | null>,
  craftingIngredients: { 1: null, 2: null } as { 1: string | null; 2: string | null },
  forgedResult: null as string | null,
};

export function initPlayerInventory() {
  if (gameSyncState.backendSynced) return;
  playerState.inventorySlots[0] = { name: 'WoodClub', count: 1, isDefault: true };
}

function energyRegenSeconds() {
  return isBackendEnabled() ? gameSyncState.energyRegenSeconds : LOCAL_ENERGY_REGEN_SECONDS;
}

function scheduleNextEnergyTick() {
  if (playerState.energy >= playerState.maxEnergy) {
    playerState.nextEnergyAt = null;
    return;
  }
  playerState.nextEnergyAt = new Date(Date.now() + energyRegenSeconds() * 1000).toISOString();
}

export function regenCountdownText() {
  const { energy, maxEnergy, nextEnergyAt } = playerState;
  if (energy >= maxEnergy || !nextEnergyAt) return '';
  const remainingMs = new Date(nextEnergyAt).getTime() - Date.now();
  if (remainingMs <= 0) return '0:00';
  const totalSec = Math.max(0, Math.ceil(remainingMs / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function refreshHud() {
  emitGame(GameEvents.HUD_REFRESH);
}

export function refreshHudDebounced() {
  refreshHudCoalesced(refreshHud);
}

export function applyProfileFromServer(
  profile: {
    energy: number;
    maxEnergy: number;
    dust: number;
    dustCollected: number;
    nextEnergyAt: string | null;
    energyUpdatedAt?: string | null;
  },
  options: { deferDustGrant?: number } = {},
) {
  const deferDustGrant = Math.max(0, Number(options.deferDustGrant ?? 0));
  playerState.energy = profile.energy;
  playerState.maxEnergy = profile.maxEnergy;

  if (deferDustGrant > 0) {
    gameSyncState.pendingBreakDust = deferDustGrant;
    playerState.dust = profile.dust - deferDustGrant;
    playerState.dustCollected = profile.dustCollected - deferDustGrant;
  } else {
    gameSyncState.pendingBreakDust = 0;
    playerState.dust = profile.dust;
    playerState.dustCollected = profile.dustCollected;
  }
  gameSyncState.dustCollected = playerState.dustCollected;

  if (profile.nextEnergyAt !== undefined) playerState.nextEnergyAt = profile.nextEnergyAt;
  if (profile.energyUpdatedAt !== undefined) playerState.energyUpdatedAt = profile.energyUpdatedAt ?? null;
  if (profile.energy >= profile.maxEnergy) {
    playerState.nextEnergyAt = null;
    playerState.energyUpdatedAt = null;
  }

  gameSyncState.lastServerProfileDust = profile.dust;
}

export function applyInventoryFromServer(
  items: Array<{ name: string; quantity: number; weaponId?: number; itemId?: number; hp?: number }>,
  { merge = false } = {},
) {
  if (!merge) {
    const slots: Array<InventorySlot | null> = [{ name: 'WoodClub', count: 1, isDefault: true }];
    const map: Record<string, number> = {};

    for (const item of items) {
      if (!item.name || item.quantity <= 0 || item.name === 'WoodClub') continue;
      map[item.name] = (map[item.name] ?? 0) + item.quantity;
      const slot: InventorySlot = { name: item.name, count: item.quantity };
      if (item.weaponId != null) {
        slot.weaponId = item.weaponId;
        gameSyncState.weaponIdByName[item.name] = item.weaponId;
      }
      if (item.itemId != null) slot.itemId = item.itemId;
      slots.push(slot);
    }

    while (slots.length < INVENTORY_SLOT_COUNT) slots.push(null);
    playerState.inventory = map;
    playerState.inventorySlots = slots;
    return;
  }

  for (const item of items) {
    if (!item.name) continue;
    if (!playerState.inventory[item.name]) playerState.inventory[item.name] = 0;
    playerState.inventory[item.name] = item.quantity;
    if (item.weaponId != null) gameSyncState.weaponIdByName[item.name] = item.weaponId;

    let found = false;
    for (const slot of playerState.inventorySlots) {
      if (slot?.name === item.name) {
        slot.count = item.quantity;
        found = true;
        break;
      }
    }
    if (!found && item.quantity > 0) {
      const emptyIdx = playerState.inventorySlots.findIndex((s) => s === null);
      const entry: InventorySlot = { name: item.name, count: item.quantity };
      if (item.weaponId != null) entry.weaponId = item.weaponId;
      if (emptyIdx >= 0) playerState.inventorySlots[emptyIdx] = entry;
      else playerState.inventorySlots.push(entry);
    }
  }
}

export function applyEquippedFromServer(weaponName: string | null) {
  if (!weaponName) return;
  if (playerState.equipped.weapon !== weaponName) {
    playerState.equipped.weapon = weaponName;
  }
}

export function consumeEnergy(amount = ENERGY_PER_TAP) {
  if (playerState.energy <= 0) return false;
  const wasFull = playerState.energy >= playerState.maxEnergy;
  playerState.energy = Math.max(0, playerState.energy - amount);
  if (wasFull && playerState.energy < playerState.maxEnergy) {
    scheduleNextEnergyTick();
  }
  refreshHudDebounced();
  return true;
}

export function addDust(amount: number) {
  playerState.dust += amount;
  playerState.dustCollected += amount;
  gameSyncState.dustCollected = playerState.dustCollected;
  refreshHudDebounced();
}

/** Grant remaining deferred break dust (e.g. new geode spawned before pickup finished). */
export function releaseUncollectedBreakDust() {
  const pending = gameSyncState.pendingBreakDust;
  if (pending <= 0) return;
  gameSyncState.pendingBreakDust = 0;
  playerState.dust += pending;
  playerState.dustCollected += pending;
  gameSyncState.dustCollected = playerState.dustCollected;
  refreshHudDebounced();
}

/** +1 dust when the player taps or auto-collects a break particle. */
export function grantOneBreakDust() {
  if (gameSyncState.pendingBreakDust <= 0) return false;
  gameSyncState.pendingBreakDust -= 1;
  playerState.dust += 1;
  playerState.dustCollected += 1;
  gameSyncState.dustCollected = playerState.dustCollected;
  refreshHudDebounced();
  return true;
}

export function addInventoryItem(name: string, amount = 1) {
  if (!playerState.inventory[name]) playerState.inventory[name] = 0;
  playerState.inventory[name] += amount;

  let remaining = amount;
  for (const slot of playerState.inventorySlots) {
    if (slot && slot.name === name && !slot.isDefault) {
      slot.count += remaining;
      remaining = 0;
      break;
    }
  }
  if (remaining > 0) {
    const empty = playerState.inventorySlots.findIndex((s) => s === null);
    if (empty >= 0) {
      playerState.inventorySlots[empty] = { name, count: remaining };
    }
  }
  refreshHudDebounced();
}

export function spendDust(amount: number) {
  playerState.dust = Math.max(0, playerState.dust - amount);
  refreshHudDebounced();
}

export function tickEnergyRegen() {
  if (isBackendEnabled()) return;
  const { energy, maxEnergy, nextEnergyAt } = playerState;
  if (energy >= maxEnergy || !nextEnergyAt) return;
  if (Date.now() < new Date(nextEnergyAt).getTime()) return;

  playerState.energy = Math.min(maxEnergy, energy + 1);
  if (playerState.energy >= maxEnergy) {
    playerState.nextEnergyAt = null;
  } else {
    scheduleNextEnergyTick();
  }
  refreshHudDebounced();
}
